/**
 * graph-layout.ts — Pure layout function for the InfluenceGraphView.
 *
 * Timeline cronologica orizzontale + beeswarm verticale. See Fase D2 plan.
 *
 * X axis: **data-driven** piecewise linear scale. Epochs are computed from
 * the actual author distribution — boundaries at natural historical
 * breakpoints (anno 0, 1400, 1700) that fall within the data range, with
 * weights proportional to 60% author density + 40% temporal span. This
 * avoids wasting space on empty ranges (e.g., post-1910 when no authors
 * are born after 1906).
 *
 * Y axis: beeswarm — all nodes start at vertical center and are pushed
 * up/down greedily until they don't collide with previously placed nodes.
 * Stable iff the input is sorted by X (we sort by anno_nascita before placing).
 *
 * Edges: TIPIZZATI per relazione (un path per source,target,kind), non più
 * l'unione indistinta. Tre tipi:
 *   - maestro_allievo → da maestri ∪ allievi (coppia non orientata,
 *     ri-orientata più-vecchio → più-giovane = maestro → allievo)
 *   - influenzato_da  → da influenzato_da (diretto)
 *   - ha_influenzato  → da ha_influenzato (diretto)
 * Ogni tipo ha la sua tinta (RELATION_COLORS), condivisa da SVG e legenda.
 */

import type { PhilosopherLocalized, ProblemaFondamentale } from '@/data/schema';
import { epochForYear } from './author-sphere';
import type { EpochKey } from '@/data/category-colors';

export interface GraphNode {
  id: string;
  name: string;
  /** Compact label for display on the graph (usually just the last name). */
  displayName: string;
  x: number;
  y: number;
  year: number;
  /** Epoca storica derivata dall'anno di nascita → tinta della sfera. */
  period: EpochKey;
  obbligatorio: boolean;
  problema: ProblemaFondamentale | null;
  imageUrl: string;
}

/** Tipi di relazione tra filosofi mostrati nel grafo. */
export type RelationKind = 'maestro_allievo' | 'influenzato_da' | 'ha_influenzato';

/** Tinta dell'arco per tipo di relazione — usata sia in SVG sia in legenda.
 *  (L'iris del brand resta riservato a selezione/nodo, mai come relazione.) */
export const RELATION_COLORS: Record<RelationKind, string> = {
  maestro_allievo: '#1d7d8c', // teal
  influenzato_da: '#b8842e',  // ambra/ocra
  ha_influenzato: '#8a5aa6',  // ametista
};

export const RELATION_ORDER: ReadonlyArray<RelationKind> = [
  'maestro_allievo',
  'influenzato_da',
  'ha_influenzato',
];

/**
 * Compact name for the graph label. The full `nome` would overlap with
 * neighbors in dense clusters (1700–1900). We use heuristics:
 *
 *   - single-token names → kept as is (Platone, Kant, Talete)
 *   - "Nome d'Luogo" / "Nome di Luogo" patterns → keep the first token
 *     (Agostino d'Ippona → "Agostino", Anselmo d'Aosta → "Anselmo")
 *   - canonical "always say full" names → preserved as full
 *     (Tommaso d'Aquino, Pico della Mirandola)
 *   - otherwise → last token (Immanuel Kant → "Kant")
 */
export function computeDisplayName(nome: string): string {
  const trim = nome.trim();

  // Canonical full-name exceptions (checked first so they beat the "Nome d'X" rule).
  const keepFull = [
    /tommaso\s+d['’]aquino/i,
    /pico\s+della\s+mirandola/i,
    /anselmo\s+d['’]aosta/i,
  ];
  for (const re of keepFull) {
    if (re.test(trim)) return trim;
  }

  // "Nome (d'|di|da) Luogo" → use just the given name.
  const diMatch = trim.match(/^(\S+)\s+(d['’]|di|da)\s/i);
  if (diMatch) return diMatch[1];

  const tokens = trim.split(/\s+/);
  if (tokens.length === 1) return trim;
  return tokens[tokens.length - 1];
}

export interface GraphEdge {
  sourceId: string;
  targetId: string;
  kind: RelationKind;
  sx: number;
  sy: number;
  tx: number;
  ty: number;
}

export interface GraphAxisTick {
  year: number;
  x: number;
  label: string;
}

export interface GraphLayout {
  nodes: GraphNode[];
  edges: GraphEdge[];
  bounds: { width: number; height: number };
  axisTicks: GraphAxisTick[];
}

export interface LayoutOptions {
  width: number;
  height: number;
  padding: { top: number; right: number; bottom: number; left: number };
  nodeRadius: number;
}

interface Epoch {
  from: number;
  to: number;
  weight: number;
}

/** Natural historical breakpoints used to split the timeline into epochs. */
const HISTORICAL_BREAKS = [0, 1400, 1700];

/** Density vs span balance: 0 = pure span, 1 = pure density. */
const DENSITY_WEIGHT = 0.6;

/**
 * Compute epochs from the actual author birth years. Boundaries are placed
 * at natural historical breakpoints that fall within the data range, with
 * weights proportional to a mix of author density and temporal span.
 *
 * This avoids wasting space on empty year ranges (e.g., 1910–2050 when
 * no author is born past 1906) and gives denser clusters (1700–1900)
 * proportionally more room.
 */
function computeEpochs(years: number[]): Epoch[] {
  if (years.length === 0) return [{ from: -700, to: 2000, weight: 1 }];

  const sorted = [...years].sort((a, b) => a - b);
  const minY = sorted[0] - 60;
  const maxY = sorted[sorted.length - 1] + 60;

  // Build boundaries: [minY, ...breaks that fall inside range..., maxY]
  const breaks = HISTORICAL_BREAKS.filter((b) => b > minY && b < maxY);
  const boundaries = [minY, ...breaks, maxY];

  // Count authors per segment
  const totalAuthors = years.length;
  const totalSpan = maxY - minY;
  const epochs: Epoch[] = [];

  for (let i = 0; i < boundaries.length - 1; i++) {
    const from = boundaries[i];
    const to = boundaries[i + 1];
    const count = years.filter((y) => y >= from && y < to).length;
    const span = to - from;

    // Blended weight: density + span (neither pure-equal-space nor pure-timeline)
    const densityPart = totalAuthors > 0 ? count / totalAuthors : 0;
    const spanPart = totalSpan > 0 ? span / totalSpan : 0;
    const weight = DENSITY_WEIGHT * densityPart + (1 - DENSITY_WEIGHT) * spanPart;

    epochs.push({ from, to, weight: Math.max(weight, 0.02) }); // floor at 2% to avoid collapse
  }

  // Normalize weights to sum to 1.
  const wSum = epochs.reduce((s, e) => s + e.weight, 0);
  for (const e of epochs) e.weight /= wSum;

  return epochs;
}

function yearToRatio(year: number, epochs: Epoch[]): number {
  let cumStart = 0;
  for (const e of epochs) {
    if (year <= e.to) {
      const clamped = Math.max(year, e.from);
      const local = (clamped - e.from) / (e.to - e.from);
      return cumStart + local * e.weight;
    }
    cumStart += e.weight;
  }
  return 1;
}

/**
 * Generate axis ticks that fall inside the data range. Uses century markers
 * in the dense modern era and sparser markers in antiquity.
 */
function computeAxisTicks(
  epochs: Epoch[],
  plotLeft: number,
  plotWidth: number,
): GraphAxisTick[] {
  const minY = epochs[0].from;
  const maxY = epochs[epochs.length - 1].to;

  // Candidate ticks: every 200 years for < 0, every 500 for 0-1500, every 100 for 1500+.
  const candidates: number[] = [];
  for (let y = Math.ceil(minY / 200) * 200; y <= maxY; y += 200) {
    if (y < 0) candidates.push(y);
  }
  for (let y = 0; y <= Math.min(maxY, 1500); y += 500) {
    candidates.push(y);
  }
  for (let y = 1500; y <= maxY; y += 100) {
    if (!candidates.includes(y)) candidates.push(y);
  }

  return candidates
    .filter((y) => y >= minY && y <= maxY)
    .sort((a, b) => a - b)
    .map((year) => ({
      year,
      x: plotLeft + yearToRatio(year, epochs) * plotWidth,
      label: year < 0 ? `${Math.abs(year)} a.C.` : String(year),
    }));
}

export function computeLayout(
  authors: PhilosopherLocalized[],
  options: LayoutOptions,
): GraphLayout {
  const { width, height, padding, nodeRadius } = options;
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const centerY = padding.top + plotHeight / 2;

  // ── Ingombro REALE del nodo (sfera + ETICHETTA sotto) ────────────────────
  // Il vecchio beeswarm usava una sola distanza circolare (raggio×2.3): le
  // sfere non si toccavano ma i NOMI sotto — larghi e di lunghezza variabile —
  // si sovrapponevano nei grappoli densi (1700–1900). Ora ogni nodo occupa un
  // BOX allineato agli assi: largo quanto il nome (nomi lunghi ⇒ più spazio),
  // alto quanto sfera + nome + anno. La collisione confronta i box, così due
  // filosofi si separano in orizzontale se lo spazio c'è, in verticale se no.
  const NAME_CHAR_W = 7.2;   // avanzamento medio di .node-label (13px, 600)
  const LABEL_TOP = -nodeRadius - 2;   // bordo alto del box (sopra la sfera)
  const LABEL_BOTTOM = 36;             // bordo basso: solo il NOME@38 (l'anno per-nodo
                                       // è ridondante con l'asse X → rimosso dal nodo)
  const BOX_H = LABEL_BOTTOM - LABEL_TOP; // altezza verticale del box
  const GAP_X = 9;           // aria minima orizzontale tra due box
  const GAP_Y = 6;           // aria minima verticale
  const halfWidthFor = (name: string): number =>
    Math.max(nodeRadius, (name.length * NAME_CHAR_W) / 2);

  // Compute data-driven epochs from the actual year distribution.
  const allYears = authors.map((a) => a.anno_nascita);
  const epochs = computeEpochs(allYears);

  // Sort by birth year for stable beeswarm placement (earliest first).
  const sorted = [...authors].sort((a, b) => a.anno_nascita - b.anno_nascita);

  const placed: GraphNode[] = [];
  const idToNode = new Map<string, GraphNode>();
  // Box di ingombro dei nodi già posati (indice allineato a `placed`).
  const placedBoxes: Array<{ x: number; y: number; halfW: number }> = [];

  for (const a of sorted) {
    const ratio = yearToRatio(a.anno_nascita, epochs);
    const x = padding.left + ratio * plotWidth;
    const halfW = halfWidthFor(computeDisplayName(a.nome));

    // Candidate Y positions: spiral outward from center in small steps; il box
    // sotto è alto ~BOX_H, quindi i passi fini permettono un impacchettamento
    // stretto pur rispettando l'ingombro reale.
    const maxOffset = plotHeight / 2 - nodeRadius;
    const step = Math.max(18, nodeRadius * 0.9);
    const candidates: number[] = [centerY];
    for (let offset = step; offset <= maxOffset; offset += step) {
      candidates.push(centerY + offset);
      candidates.push(centerY - offset);
    }

    let bestCy = centerY;
    let bestPenalty = Infinity;
    for (const cy of candidates) {
      let penalty = 0;
      for (const p of placedBoxes) {
        // Overlap AABB: orizzontale sui semi-larghezze dei nomi, verticale
        // sull'altezza del box (sfera + etichetta). Con margini GAP_*.
        const needX = p.halfW + halfW + GAP_X;
        const needY = BOX_H + GAP_Y;
        const overX = needX - Math.abs(p.x - x);
        const overY = needY - Math.abs(p.y - cy);
        if (overX > 0 && overY > 0) penalty += Math.min(overX, overY); // area di intrusione
      }
      if (penalty === 0) {
        bestCy = cy;
        break; // slot pulito: lo prendiamo (i candidati sono già ordinati vicino→lontano)
      }
      if (penalty < bestPenalty) {
        bestPenalty = penalty;
        bestCy = cy; // fallback: il MENO sovrapposto (mai un pile-up al centro)
      }
    }
    const y = bestCy;
    placedBoxes.push({ x, y, halfW });

    const node: GraphNode = {
      id: a.id,
      name: a.nome,
      displayName: computeDisplayName(a.nome),
      x,
      y,
      year: a.anno_nascita,
      period: epochForYear(a.anno_nascita),
      obbligatorio: a.obbligatorio_ministero,
      problema: a.problemi_fondamentali_iniziali[0] ?? null,
      imageUrl: `/images/authors/${a.id}.webp`,
    };
    placed.push(node);
    idToNode.set(a.id, node);
  }

  // Edges TIPIZZATI (un path per source,target,kind). Dedupe per (kind, pair):
  // così tra due nodi possono convivere più tipi (es. maestro_allievo +
  // influenzato_da) con colori distinti, ma non due copie dello stesso tipo.
  const seen = new Set<string>();
  const edges: GraphEdge[] = [];

  const pushEdge = (kind: RelationKind, srcId: string, tgtId: string): void => {
    if (!idToNode.has(srcId) || !idToNode.has(tgtId) || srcId === tgtId) return;
    const key = `${kind}|${srcId}|${tgtId}`;
    if (seen.has(key)) return;
    seen.add(key);
    const s = idToNode.get(srcId)!;
    const t = idToNode.get(tgtId)!;
    edges.push({ sourceId: s.id, targetId: t.id, kind, sx: s.x, sy: s.y, tx: t.x, ty: t.y });
  };

  // maestro_allievo: unione non orientata di maestri ∪ allievi, ri-orientata
  // più-vecchio → più-giovane (il maestro precede l'allievo nel tempo).
  const maPairs = new Set<string>();
  for (const a of authors) {
    if (!idToNode.has(a.id)) continue;
    for (const oid of [...a.maestri, ...a.allievi]) {
      if (!idToNode.has(oid) || oid === a.id) continue;
      const [id1, id2] = a.id < oid ? [a.id, oid] : [oid, a.id];
      const pairKey = `${id1}|${id2}`;
      if (maPairs.has(pairKey)) continue;
      maPairs.add(pairKey);
      const nA = idToNode.get(id1)!;
      const nB = idToNode.get(id2)!;
      const older = nA.year <= nB.year ? nA : nB;
      const younger = nA.year <= nB.year ? nB : nA;
      pushEdge('maestro_allievo', older.id, younger.id);
    }
  }

  // influenzato_da / ha_influenzato: diretti, orientati come dichiarati
  // (la semantica differisce per tipo). source = il filosofo `a`.
  for (const a of authors) {
    if (!idToNode.has(a.id)) continue;
    for (const oid of a.influenzato_da) pushEdge('influenzato_da', a.id, oid);
    for (const oid of a.ha_influenzato) pushEdge('ha_influenzato', a.id, oid);
  }

  // Axis ticks — auto-computed from data range and density.
  const axisTicks = computeAxisTicks(epochs, padding.left, plotWidth);

  return {
    nodes: placed,
    edges,
    bounds: { width, height },
    axisTicks,
  };
}
